import { useContext, useState } from "react";
import { RightPanelLayout, useIsMobileRightPanelLayout } from "./right-panel-layout/RightPanelLayout";
import { KmsTypeComponent, KmsTypeHeroProps } from "@kaltura/mediaspace-shared-types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { MediaMetadataContainer } from "./media-metadata/MediaMetadata";
import { MediaModuleDataContainer } from "./media-module-data/MediaModuleData";
import { MediaTitleAreaContainer } from "./title-area/MediaTitleAreaContainer";
import { SidebarContainer } from "./sidebar/Sidebar";
import { Hero } from "./hero/Hero";
import { FeatureContainer } from "@kaltura/mediaspace-shared-styled";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { MediaDrawerProvider } from "./media-drawer/MediaDrawerContext";
import { MediaDrawer } from "./media-drawer/MediaDrawer";

export interface MediaPageProps<T extends KmsTypeHeroProps> extends KmsTypeComponent<T> {
    props: T;
    isReadOnly?: boolean;
}

/**
 * main Media Page Component - renders the page layout and the Hero component.
 * used for media page and playlist dedicated page.
 */
export function MediaPage<T extends KmsTypeHeroProps>(props: MediaPageProps<T>) {
    const { props: componentProps, isReadOnly = false } = props;

    const [collapseHero, setCollapseHero] = useState(false);
    const [isEmptyRightPanel, setIsEmptyRightPanel] = useState(false);
    const isMobileRightPanelLayout = useIsMobileRightPanelLayout();

    // initialize with the initial, never changing entry
    const [entryId, setEntryId] = useState(componentProps.entry?.id);

    const [reloadCounter, setReloadCounter] = useState(0);
    const handleRequestEntryReload = () => setReloadCounter((counter) => counter + 1);

    // content width is different in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;
    // update the entry id of the current entry shown (playlist context)
    const handleEntryChanged = (entryId: string) => {
        setEntryId(entryId);
    };

    // media name - playlist/entry
    const mediaName = componentProps.playlist ? componentProps.playlist.name : componentProps.entry.name;

    const rightPanelComponent: KmsTypeComponent = {
        props: { ...componentProps, entryId: entryId, reloadCounter },
        component: SidebarContainer,
        name: "SidebarComponent",
    };

    return (
        // In KAF readOnly mode, the page container should look like MediaSpace (not KAF),
        // so we set isKaf to false in that case.
        <FeatureContainer className="kms-ds-media-page" isKaf={isKaf && !isReadOnly}>
            <MediaDrawerProvider>
                <RightPanelLayout
                    hero={
                        <section aria-label={translate("Hero for %1", [mediaName])}>
                            <Hero
                                {...props}
                                collapse={collapseHero && !isMobileRightPanelLayout}
                                onToggleCollapse={setCollapseHero}
                                onEntryChange={handleEntryChanged}
                            />
                        </section>
                    }
                    details={
                        <section aria-label={translate("Details for %1", [mediaName])}>
                            {entryId && (
                                <>
                                    <MediaTitleAreaContainer
                                        key={entryId}
                                        {...componentProps}
                                        entryId={entryId}
                                        isEmptyRightPanel={isEmptyRightPanel}
                                        reloadCounter={reloadCounter}
                                        onRequestEntryReload={handleRequestEntryReload}
                                        isReadOnly={isReadOnly}
                                    />
                                    <MediaMetadataContainer
                                        {...componentProps}
                                        entryId={entryId}
                                        isEmptyRightPanel={isEmptyRightPanel}
                                        reloadCounter={reloadCounter}
                                        isReadOnly={isReadOnly}
                                    />
                                    <MediaModuleDataContainer
                                        {...componentProps}
                                        entryId={entryId}
                                        reloadCounter={reloadCounter}
                                        isReadOnly={isReadOnly}
                                    />
                                </>
                            )}
                        </section>
                    }
                    rightPanel={entryId && !isReadOnly ? rightPanelComponent : undefined}
                    collapseHero={collapseHero}
                    setIsEmptyRightPanel={setIsEmptyRightPanel}
                    isEmptyRightPanel={isEmptyRightPanel}
                />
                <MediaDrawer />
            </MediaDrawerProvider>
        </FeatureContainer>
    );
}

export default MediaPage;
